import "./styles/index.css";
import { useState, useEffect } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PostHogProvider, PostHogErrorBoundary } from "posthog-js/react";
import { AuthProvider, AuthGuard } from "./features/auth";
import { ChatPage } from "./pages/ChatPage";
import { SettingsPage } from "./pages/SettingsPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { config } from "./shared/config";
import { Toaster } from "@/shared/ui/sonner";
import { AlphaBadge } from "./components/alpha-badge";

type Page = "onboarding" | "chat" | "settings";


function useRouter() {
  const getPage = (): Page => {
    const path = window.location.pathname;
    if (path === "/settings") return "settings";
    if (path === "/chat") return "chat";
    return "onboarding";
  };

  const [currentPage, setCurrentPage] = useState<Page>(getPage);

  useEffect(() => {
    const handlePopState = () => setCurrentPage(getPage());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (page: Page) => {
    const paths: Record<Page, string> = {
      onboarding: "/",
      chat: "/chat",
      settings: "/settings",
    };
    window.history.pushState(null, "", paths[page]);
    setCurrentPage(page);
  };

  return { currentPage, navigate };
}

function AuthenticatedApp() {
  const { currentPage, navigate } = useRouter();

  if (currentPage === "settings") {
    return <SettingsPage onBack={() => navigate("onboarding")} />;
  }

  if (currentPage === "chat") {
    return <ChatPage onOpenSettings={() => navigate("settings")} />;
  }

  return <OnboardingPage />;
}

export function App() {
  return (
    <PostHogProvider
      apiKey={config.posthog.apiKey}
      options={{
        api_host: config.posthog.host,
        capture_pageview: true,
        autocapture: true,
        disable_session_recording: true,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
      }}
    >
      <PostHogErrorBoundary>
        <GoogleOAuthProvider clientId={config.googleClientId}>
          <AuthProvider>
            <AuthGuard>
              <AuthenticatedApp />
            </AuthGuard>
          </AuthProvider>
          <Toaster />
          <AlphaBadge />
        </GoogleOAuthProvider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  );
}

export default App;
